import type { ReactNode } from "react";
import { cn } from "@/utils/cn";

export interface DataTableColumn<T> {
  /** Identifiant unique de colonne. */
  key: string;
  /** Libellé d'en-tête affiché sur desktop / tablette. */
  header: string;
  /** Rendu de la cellule pour une ligne donnée. */
  render: (row: T) => ReactNode;
  /** Classes appliquées à la cellule ET à l'en-tête (largeur min, etc.). */
  className?: string;
  /** Alignement du contenu. */
  align?: "left" | "center" | "right";
}

interface DataTableProps<T> {
  columns: DataTableColumn<T>[];
  rows: T[];
  rowKey: (row: T) => string;
  /**
   * Largeur minimale du tableau. Sur mobile, le tableau reste horizontal :
   * il défile à l'intérieur de son conteneur (pas de débordement de page).
   */
  minWidthClass?: string;
  /** Description accessible du tableau. */
  caption?: string;
}

const alignClass = (align: DataTableColumn<unknown>["align"]) =>
  align === "right" ? "text-right" : align === "center" ? "text-center" : "text-left";

/**
 * Tableau de données compact et responsive.
 * Desktop / tablette : colonnes alignées sur une ligne.
 * Mobile : défilement horizontal limité au tableau (aucun débordement de page).
 */
export default function DataTable<T>({
  columns,
  rows,
  rowKey,
  minWidthClass = "min-w-[720px]",
  caption,
}: DataTableProps<T>) {
  return (
    <div className="w-full overflow-x-auto rounded-lg border border-background-200 bg-background-50">
      <table className={cn("w-full border-collapse text-sm", minWidthClass)}>
        {caption && <caption className="sr-only">{caption}</caption>}
        <thead>
          <tr className="border-b border-background-200 bg-background-100/70">
            {columns.map((column) => (
              <th
                key={column.key}
                scope="col"
                className={cn(
                  "whitespace-nowrap px-4 py-3 text-xs font-label font-semibold uppercase tracking-wide text-foreground-500",
                  alignClass(column.align),
                  column.className
                )}
              >
                {column.header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr
              key={rowKey(row)}
              className="border-b border-background-200/70 transition-colors last:border-0 hover:bg-background-100/60"
            >
              {columns.map((column) => (
                <td
                  key={column.key}
                  className={cn("px-4 py-3 align-middle", alignClass(column.align), column.className)}
                >
                  {column.render(row)}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}